/**
 * /connexion-sociale — V2 Module 1.
 *
 * Lists the user's connected social accounts (Instagram, Facebook) and lets
 * them add new ones via the META OAuth flow. The connect button calls
 * POST /social/connect/instagram, which returns an authorize_url; we full-
 * redirect there so META can show its consent screen. On callback the
 * backend redirects back here with ?status=ok|error.
 */
import { useEffect, useState } from 'react';
import i18n from '@/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Plus,
  Instagram,
  Facebook,
  CheckCircle2,
  AlertCircle,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import { apiRequest } from '@/lib/api';

interface SocialAccount {
  id: string;
  platform: 'instagram' | 'facebook';
  account_id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  status: 'active' | 'expired' | 'revoked' | 'error';
  connected_at: string;
  updated_at: string;
  meta: Record<string, unknown>;
}

export default function SocialConnectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null);

  // OAuth callback toast (the backend redirects here with ?status=ok|error).
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'ok') {
      const msg = searchParams.get('msg') ?? '';
      const count = msg.startsWith('connected:') ? Number(msg.slice('connected:'.length)) : 0;
      setToast({
        kind: 'ok',
        msg: count > 0 ? t('social.connected', { count }) : t('social.connectedNone'),
      });
      void qc.invalidateQueries({ queryKey: ['social', 'accounts'] });
    } else if (status === 'error') {
      const raw = searchParams.get('msg') ?? 'unknown_error';
      setToast({ kind: 'error', msg: humaniseError(raw) });
    }
    if (status) {
      // Strip the URL params so the toast doesn't reappear on refresh.
      const clean = new URLSearchParams(searchParams);
      clean.delete('status');
      clean.delete('msg');
      setSearchParams(clean, { replace: true });
    }
  }, [searchParams, setSearchParams, qc]);

  // Auto-dismiss toast after 6s.
  useEffect(() => {
    if (!toast) return;
    const tid = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(tid);
  }, [toast]);

  const accountsQuery = useQuery<{ accounts: SocialAccount[] }>({
    queryKey: ['social', 'accounts'],
    queryFn: () => apiRequest('/social'),
  });

  const connectMutation = useMutation({
    mutationFn: (flow: 'instagram' | 'facebook') =>
      apiRequest<{ authorize_url: string }>(`/social/connect/${flow}`, { method: 'POST' }),
    onSuccess: (data) => {
      window.location.href = data.authorize_url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/social/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['social', 'accounts'] });
      setToast({ kind: 'ok', msg: t('social.disconnected') });
    },
  });

  const accounts = accountsQuery.data?.accounts ?? [];

  return (
    <div className="mx-auto max-w-[760px] px-5 py-10 sm:px-8">
      {/* Header */}
      <header className="mb-8 flex items-center gap-3.5">
        <button
          onClick={() => navigate('/')}
          className="rounded-full border border-ink/[0.12] bg-white/70 p-2 text-ink/60 transition hover:text-ink"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t('social.kicker')}
          </p>
          <h1 className="font-display text-[clamp(20px,3vw,28px)] font-800 tracking-[-0.025em]">
            {t('social.heading')}
          </h1>
          <p className="mt-1 text-[13.5px] leading-[1.55] text-ink/55">
            {t('social.subheading')}
          </p>
        </div>
      </header>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`mb-5 flex items-center gap-2.5 rounded-2xl p-3.5 ${
              toast.kind === 'ok'
                ? 'border border-emerald-300/60 bg-emerald-500/10 text-emerald-800'
                : 'border border-amber-300/60 bg-amber-500/10 text-amber-800'
            }`}
          >
            {toast.kind === 'ok' ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
            <p className="text-[13.5px] font-600">{toast.msg}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connect CTA — two flows side by side */}
      <section className="card mb-6 p-6 sm:p-7">
        <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-ink/45">
          {t('social.addAccount')}
        </p>
        <h2 className="mt-1 font-display text-[18px] font-800 tracking-[-0.015em]">
          {t('social.howConnect')}
        </h2>
        <p className="mt-1 mb-5 text-[13.5px] leading-[1.55] text-ink/65">
          {t('social.choosePlatform')}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {/* Instagram direct — disabled until Meta enables IG Login endpoint
              on our app; for now users connect via Facebook (which also exposes
              their linked IG Business accounts). */}
          <div
            aria-disabled="true"
            className="group relative flex flex-col items-start gap-2 overflow-hidden rounded-2xl border-2 border-dashed border-ink/[0.08] bg-white/40 p-5 text-left opacity-70"
          >
            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2 py-0.5 text-[10px] font-700 uppercase tracking-[0.08em] text-ink/55">
              {t('social.comingSoon')}
            </span>
            <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-pink to-violet text-white opacity-70">
              <Instagram size={22} />
            </div>
            <div className="relative">
              <p className="font-display text-[15px] font-800 tracking-[-0.01em]">Instagram</p>
              <p className="mt-0.5 text-[12.5px] leading-[1.45] text-ink/55">
                {t('social.instagramDirectSoon')}
              </p>
            </div>
          </div>

          {/* Facebook */}
          <button
            onClick={() => connectMutation.mutate('facebook')}
            disabled={connectMutation.isPending}
            className="group relative flex flex-col items-start gap-2 overflow-hidden rounded-2xl border-2 border-ink/[0.08] bg-white p-5 text-left transition hover:border-blue-500/50 hover:shadow-[0_8px_24px_-12px_rgba(59,130,246,0.35)] disabled:opacity-50"
          >
            <div
              className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-20 blur-2xl transition group-hover:opacity-40"
              style={{ background: 'radial-gradient(circle, #3b82f6, #1e40af)' }}
            />
            <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white">
              <Facebook size={22} />
            </div>
            <div className="relative">
              <p className="font-display text-[15px] font-800 tracking-[-0.01em]">Facebook</p>
              <p className="mt-0.5 text-[12.5px] leading-[1.45] text-ink/55">
                {t('social.facebookDesc')}
              </p>
            </div>
            <span className="relative mt-1 inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-[11px] font-700 text-blue-700">
              {connectMutation.isPending && connectMutation.variables === 'facebook' ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Plus size={11} />
              )}
              {t('social.connectWithFacebook')}
            </span>
          </button>
        </div>
      </section>

      {/* Accounts list */}
      <section>
        <p className="mb-3 font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-ink/45">
          {t('social.yourConnectedAccounts')}
        </p>

        {accountsQuery.isLoading && (
          <div className="card flex items-center justify-center p-10">
            <Loader2 className="animate-spin text-pink" size={26} />
          </div>
        )}

        {!accountsQuery.isLoading && accounts.length === 0 && (
          <div className="card flex flex-col items-center justify-center gap-3 p-10 text-center">
            <div className="flex gap-2">
              <Instagram size={22} className="text-pink" />
              <Facebook size={22} className="text-blue-600" />
            </div>
            <p className="text-[14px] font-600 text-ink/65">
              {t('social.noAccounts')}
            </p>
            <p className="max-w-[420px] text-[12.5px] leading-[1.55] text-ink/45">
              {t('social.noAccountsHint')}
            </p>
          </div>
        )}

        <div className="space-y-3">
          {accounts.map((acc) => (
            <AccountCard
              key={acc.id}
              account={acc}
              onDisconnect={() => disconnectMutation.mutate(acc.id)}
              disconnecting={disconnectMutation.isPending}
            />
          ))}
        </div>
      </section>

      {/* RGPD reassurance */}
      <p className="mt-6 text-center text-[11.5px] leading-[1.55] text-ink/45">
        {t('social.privacyNote')}
      </p>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function AccountCard({
  account,
  onDisconnect,
  disconnecting,
}: {
  account: SocialAccount;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  const { t } = useTranslation();
  const Icon = account.platform === 'instagram' ? Instagram : Facebook;
  const tint = account.platform === 'instagram' ? '#ec4899' : '#3b82f6';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="card flex items-center gap-4 p-4 sm:p-5"
    >
      {account.avatar_url ? (
        <img
          src={account.avatar_url}
          alt={account.display_name ?? ''}
          className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-white"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: `linear-gradient(135deg, ${tint}, #a78bfa)` }}
        >
          <Icon size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-display text-[14.5px] font-800 tracking-[-0.01em]">
            {account.display_name ?? account.handle ?? 'Compte sans nom'}
          </p>
          <StatusBadge status={account.status} />
        </div>
        <p className="mt-0.5 truncate font-mono text-[11.5px] text-ink/50">
          {account.platform === 'instagram' ? 'Instagram' : 'Facebook'}
          {account.handle ? ` · @${account.handle}` : ''}
        </p>
      </div>
      <button
        onClick={onDisconnect}
        disabled={disconnecting || account.status === 'revoked'}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/[0.08] bg-white/60 px-3 py-1.5 text-[12px] font-700 text-ink/65 transition hover:border-pink/40 hover:text-pink-deep disabled:opacity-40"
        aria-label={t('common.disconnect')}
      >
        <Trash2 size={12} />
        Déconnecter
      </button>
    </motion.div>
  );
}

function StatusBadge({ status }: { status: SocialAccount['status'] }) {
  const { t } = useTranslation();
  const map: Record<SocialAccount['status'], { label: string; color: string }> = {
    active: { label: t('social.status.active'), color: '#10b981' },
    expired: { label: t('social.status.expired'), color: '#f59e0b' },
    revoked: { label: t('social.status.revoked'), color: '#94a3b8' },
    error: { label: t('social.status.error'), color: '#ef4444' },
  };
  const { label, color } = map[status];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-700"
      style={{ background: `${color}1a`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

/** Pretty-print META error codes we surface to the user on callback. */
function humaniseError(raw: string): string {
  const map: Record<string, string> = {
    invalid_or_expired_state: i18n.t('social.errors.state'),
    callback_missing_params: i18n.t('social.errors.params'),
    user_denied: i18n.t('social.errors.denied'),
    access_denied: i18n.t('social.errors.denied'),
  };
  return map[raw] ?? i18n.t('social.errors.generic', { code: raw.slice(0, 80) });
}
